import type { ChatOnFinishCallback } from 'ai';
import { useCallback, useEffect } from 'react';

import { useNovaChat } from '../hooks/use-nova-chat';
import type { UIMessageWithMetadata } from '../lib/types';

interface OrphanedStreamRecoveryProps {
  chatUuid: string;
  chatTitle: string;
  initialMessages: UIMessageWithMetadata[];
  onStreamFinish: (params: { chatUuid: string; chatTitle: string; isStandaloneMode?: boolean }) => Promise<void>;
}

/**
 * Renderless component that recovers orphaned streams after page refresh.
 * Uses AI SDK's useNovaChat hook to automatically resume the stream and
 * get notified when it completes via onFinish callback.
 *
 * This component doesn't render anything - it's purely for side effects.
 */
export function OrphanedStreamRecovery({
  chatUuid,
  chatTitle,
  initialMessages,
  onStreamFinish,
}: OrphanedStreamRecoveryProps) {
  // Handle stream completion - this will be called by AI SDK when stream finishes
  const handleFinish: ChatOnFinishCallback<UIMessageWithMetadata> = useCallback(async () => {
    await onStreamFinish({
      chatUuid,
      chatTitle,
    });
  }, [chatUuid, chatTitle, onStreamFinish]);

  // Use Nova chat hook - this will automatically resume the stream via useAutoResume
  // and call handleFinish when the stream completes
  const chat = useNovaChat({
    chatUuid,
    initialMessages,
    onFinish: handleFinish,
  });

  useEffect(() => {
    chat.resumeStream();
  }, []);

  // Render nothing - this is a renderless component
  return null;
}
